import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * Lunora — Cinematic Gender Selection (luxury editorial).
 * "Sabyasachi meets modern AI luxury tailoring."
 *
 * Composition decisions:
 *  · Two cinematic vertical halves (mobile-first portrait, also great on desktop)
 *  · Curated Pexels editorial portraits (Indian bridal couture + golden sherwani)
 *  · UNIFIED color grade applied via CSS filter for one-campaign consistency
 *  · Warm linear overlay (not pure black) → preserves richness, kills muddiness
 *  · Editorial Nº 01 / Nº 02 markers + Playfair numeral + gold hairline
 *  · Letter-stagger reveal, ken-burns slow zoom, gold shimmer underline
 */

// ─────────────────────────────────────────────────────────────────────────────
// EDITORIAL ASSETS — Pexels (free, commercial use, high-resolution)
// Women: soft illuminated Indian bride in traditional red, intricate jewellery.
// Men:   ivory & deep-gold royal sherwani + powerful full-body posture in a
//        historic stone archway (palace architecture, editorial campaign feel).
// ─────────────────────────────────────────────────────────────────────────────
const BANNER_W =
  'https://static.prod-images.emergentagent.com/jobs/1b8ac37b-5db7-4b09-a3c4-0dc2e901c052/images/d8d7749adfbbbd70a6c494bb48383ad70df512bc7f7df3bd27b975645bec81e1.jpeg';
const BANNER_M =
  'https://static.prod-images.emergentagent.com/jobs/1b8ac37b-5db7-4b09-a3c4-0dc2e901c052/images/570ff6fd0e6ff5a28c64be466a40a38f169dea3290a6229c9550f2117e000ac6.jpeg';

// Unified color grade — applied to BOTH halves so they read as one campaign.
const LUXURY_GRADE = 'brightness(1.02) contrast(1.06) saturate(1.08)';

// Warm, editorial overlay — protects legibility without going jet-black.
// Top kept very light (image breathes), bottom rich for text contrast.
const OVERLAY_W =
  'linear-gradient(180deg, rgba(38,18,12,0.10) 0%, rgba(28,14,10,0.30) 45%, rgba(15,9,8,0.82) 100%)';
// Men overlay — kept lighter at top so the ivory/gold sherwani breathes,
// then rolls into a rich palace-shadow black for crisp text legibility.
const OVERLAY_M =
  'linear-gradient(180deg, rgba(18,12,6,0.10) 0%, rgba(12,8,4,0.58) 48%, rgba(8,6,4,0.94) 100%)';

const Side = ({
  side,
  image,
  numeral,
  subhead,
  overlay,
  onSelect,
  testId,
  delayMs = 0,
}) => {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  return (
    <button
      data-testid={testId}
      onClick={onSelect}
      className="relative flex-1 overflow-hidden group focus:outline-none"
      style={{ minHeight: '50vh' }}
    >
      {/* Editorial photograph w/ unified luxury grade + ken-burns */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${image})`,
          backgroundPosition: side === 'w' ? 'center 25%' : 'center 22%',
          filter: LUXURY_GRADE,
          transform: revealed ? 'scale(1.04)' : 'scale(1.0)',
          opacity: revealed ? 1 : 0,
          transition: 'opacity 1400ms ease-out, transform 1400ms ease-out',
          animation: revealed
            ? `lunora-kenburns-${side} 18s ease-in-out infinite alternate`
            : 'none',
          willChange: 'transform, opacity',
        }}
      />

      {/* Soft warm overlay (top → bottom) for text legibility */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: overlay }} />

      {/* Editorial vignette + film-grain feel */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.45) 100%)',
          mixBlendMode: 'multiply',
          opacity: 0.55,
        }}
      />

      {/* Centered text block */}
      <div className="absolute inset-0 flex flex-col items-center justify-end px-6 pb-12 sm:pb-14 text-center">
        <div
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(10px)',
            transition: 'opacity 900ms ease-out, transform 900ms cubic-bezier(0.4,0,0.2,1)',
            transitionDelay: '450ms',
          }}
        >
          {/* Eyebrow — gold tracked caps */}
          <p
            className="mb-3"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontSize: '10px',
              letterSpacing: '0.42em',
              textTransform: 'uppercase',
              color: '#D4AF37',
              textShadow: '0 1px 4px rgba(0,0,0,0.6)',
            }}
          >
            The House of Lunora
          </p>

          {/* Numeral — Playfair display, letter-stagger reveal */}
          <h2
            data-testid={`${testId}-numeral`}
            className="leading-none mb-4"
            style={{
              fontFamily: 'Playfair Display, serif',
              fontWeight: 300,
              fontSize: 'clamp(64px, 16vw, 104px)',
              color: '#F8F4ED',
              letterSpacing: '-0.015em',
              textShadow: '0 2px 24px rgba(0,0,0,0.55)',
            }}
          >
            {numeral.split('').map((ch, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  opacity: revealed ? 1 : 0,
                  transform: revealed ? 'translateY(0)' : 'translateY(14px)',
                  transition:
                    'opacity 750ms cubic-bezier(0.4,0,0.2,1), transform 750ms cubic-bezier(0.4,0,0.2,1)',
                  transitionDelay: `${800 + i * 70}ms`,
                }}
              >
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </h2>

          {/* Gold shimmer hairline */}
          <div
            className="mx-auto mb-4 lunora-shimmer"
            style={{
              width: '72px',
              height: '1.5px',
              background:
                'linear-gradient(90deg, transparent 0%, #D4AF37 50%, transparent 100%)',
              backgroundSize: '200% 100%',
            }}
          />

          {/* Italic subhead */}
          <p
            style={{
              fontFamily: 'Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: 'clamp(13px, 3.4vw, 15px)',
              color: 'rgba(248,244,237,0.94)',
              letterSpacing: '0.05em',
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 700ms ease-out, transform 700ms ease-out',
              transitionDelay: '1300ms',
              textShadow: '0 1px 8px rgba(0,0,0,0.65)',
            }}
          >
            {subhead}
          </p>

          {/* CTA chip */}
          <div
            className="mt-7 inline-flex items-center gap-2 px-5 py-2.5 group-active:scale-[0.97]"
            style={{
              border: '1px solid rgba(212,175,55,0.7)',
              color: '#F8F4ED',
              fontFamily: 'Outfit, sans-serif',
              fontSize: '10px',
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              backdropFilter: 'blur(3px)',
              background: 'rgba(255,255,255,0.05)',
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateY(0)' : 'translateY(6px)',
              transition:
                'opacity 700ms ease-out, transform 220ms ease-out, background 220ms ease-out, border-color 220ms ease-out',
              transitionDelay: '1600ms',
            }}
          >
            Enter Collection
            <ArrowRight className="w-3 h-3" strokeWidth={1.6} />
          </div>
        </div>
      </div>
    </button>
  );
};

const GenderSelection = () => {
  const navigate = useNavigate();
  const select = (gender) => navigate('/category-selection', { state: { gender } });

  return (
    <div
      data-testid="gender-selection"
      className="h-screen w-full flex flex-col relative"
      style={{ background: '#0B0807', fontFamily: 'Outfit, sans-serif' }}
    >
      {/* Top brand wordmark */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-center pt-5 pointer-events-none">
        <p
          data-testid="lunora-wordmark"
          className="uppercase"
          style={{
            fontSize: '10px',
            letterSpacing: '0.6em',
            color: 'rgba(248,244,237,0.78)',
            textShadow: '0 1px 6px rgba(0,0,0,0.5)',
          }}
        >
          L · U · N · O · R · A
        </p>
      </div>

      {/* Two cinematic halves */}
      <div className="flex-1 flex flex-col">
        <Side
          side="w"
          testId="gender-women-btn"
          image={BANNER_W}
          numeral="WOMEN"
          subhead="Couture Crafted To Your Measure"
          overlay={OVERLAY_W}
          onSelect={() => select('female')}
          delayMs={120}
        />

        {/* Editorial divider with brand emblem */}
        <div
          aria-hidden="true"
          className="relative z-20"
          style={{
            height: 1,
            background:
              'linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.55) 50%, transparent 100%)',
          }}
        >
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#0B0807',
              border: '1px solid rgba(212,175,55,0.6)',
              boxShadow: '0 0 24px rgba(212,175,55,0.18)',
            }}
          >
            <span
              style={{
                fontFamily: 'Playfair Display, serif',
                fontStyle: 'italic',
                fontSize: 16,
                color: '#D4AF37',
                lineHeight: 1,
                marginTop: -1,
              }}
            >
              L
            </span>
          </div>
        </div>

        <Side
          side="m"
          testId="gender-men-btn"
          image={BANNER_M}
          numeral="MEN"
          subhead="Bespoke Royal Tailoring, Reimagined"
          overlay={OVERLAY_M}
          onSelect={() => select('male')}
          delayMs={320}
        />
      </div>

      {/* Local keyframes — cinematic ken-burns + gold shimmer */}
      <style>{`
        @keyframes lunora-kenburns-w {
          0%   { transform: scale(1.04) translate(0, 0); }
          100% { transform: scale(1.10) translate(-1.5%, -1%); }
        }
        @keyframes lunora-kenburns-m {
          0%   { transform: scale(1.04) translate(0, 0); }
          100% { transform: scale(1.09) translate(1.5%, -1%); }
        }
        @keyframes lunora-shimmer-sweep {
          0%, 100% { background-position: 200% 0; opacity: 0.65; }
          50%      { background-position: -200% 0; opacity: 1; }
        }
        .lunora-shimmer { animation: lunora-shimmer-sweep 3.4s ease-in-out infinite; }

        /* Hover polish (desktop) */
        @media (hover: hover) {
          [data-testid="gender-women-btn"]:hover img,
          [data-testid="gender-men-btn"]:hover img { transform: scale(1.06); }
        }
      `}</style>
    </div>
  );
};

export default GenderSelection;
